/** Pure helpers for the storage screen: folder tree lookups, move targets, upload checks, selection */

import { isGenericDescription, normalizeDescription } from '@agentwiki/shared'
import type { StorageFolderNode } from '../../hooks/use-storage'

export const MAX_UPLOAD_BYTES = 100 * 1024 * 1024 // 100MB, same limit as the API
export const MAX_DESCRIPTION_CHARS = 1000

/** id -> folder for every node in the tree */
export function indexFolders(tree: StorageFolderNode[]): Map<string, StorageFolderNode> {
  const map = new Map<string, StorageFolderNode>()
  const walk = (nodes: StorageFolderNode[]) => {
    for (const n of nodes) {
      map.set(n.id, n)
      walk(n.children)
    }
  }
  walk(tree)
  return map
}

/** Direct children of a folder (root = top-level folders) */
export function getSubfolders(tree: StorageFolderNode[], folderId: string | null): StorageFolderNode[] {
  if (!folderId) return tree
  return indexFolders(tree).get(folderId)?.children ?? []
}

/** Root-to-folder path; empty for the root or an unknown id */
export function getFolderPath(tree: StorageFolderNode[], folderId: string | null): StorageFolderNode[] {
  if (!folderId) return []
  const map = indexFolders(tree)
  const path: StorageFolderNode[] = []
  const seen = new Set<string>()
  let current = map.get(folderId)
  while (current && !seen.has(current.id)) {
    seen.add(current.id)
    path.unshift(current)
    current = current.parentId ? map.get(current.parentId) : undefined
  }
  return path
}

export interface FolderOption {
  id: string
  name: string
  depth: number
}

/**
 * Folders that selected items may be moved into, in tree order.
 * Excludes the moved folders themselves and everything below them (the API rejects cycles).
 */
export function moveTargetOptions(tree: StorageFolderNode[], movingFolderIds: Iterable<string>): FolderOption[] {
  const excluded = new Set(movingFolderIds)
  const options: FolderOption[] = []
  const walk = (nodes: StorageFolderNode[], depth: number) => {
    for (const n of nodes) {
      if (excluded.has(n.id)) continue
      options.push({ id: n.id, name: n.name, depth })
      walk(n.children, depth + 1)
    }
  }
  walk(tree, 0)
  return options
}

/** Split picked files into uploadable ones and those over the size limit */
export function partitionBySize(files: File[], maxBytes = MAX_UPLOAD_BYTES): { valid: File[]; oversized: File[] } {
  const valid: File[] = []
  const oversized: File[] = []
  for (const f of files) (f.size > maxBytes ? oversized : valid).push(f)
  return { valid, oversized }
}

export type DescriptionProblem = 'required' | 'tooLong' | 'generic'

/** Mirrors the API's required-description rule so the form can explain a rejection before sending */
export function descriptionProblem(value: string): DescriptionProblem | null {
  const normalized = normalizeDescription(value)
  if (!normalized) return 'required'
  if (normalized.length > MAX_DESCRIPTION_CHARS) return 'tooLong'
  if (isGenericDescription(normalized)) return 'generic'
  return null
}

/** Returns a new set with the id added or removed */
export function toggleId(set: ReadonlySet<string>, id: string): Set<string> {
  const next = new Set(set)
  if (next.has(id)) next.delete(id)
  else next.add(id)
  return next
}

/** Checkbox state for "select all in view" */
export function selectAllState(selectedCount: number, visibleCount: number): 'none' | 'some' | 'all' {
  if (selectedCount === 0 || visibleCount === 0) return 'none'
  return selectedCount >= visibleCount ? 'all' : 'some'
}
